import * as DialogPrimitive from '@radix-ui/react-dialog'
import camelcaseKeys from 'camelcase-keys'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CircleCheckIcon,
  ExternalLinkIcon,
  PencilIcon,
  RotateCcwIcon,
  XIcon,
} from 'lucide-react'
import { Suspense, useEffect, useRef, useState } from 'react'

import {
  Button,
  DialogDescription,
  DialogOverlay,
  DialogTitle,
} from '@/components/ui'
import { useToast } from '@/hooks'
import { useReconciliationEntitled } from '@/modules/account'
import {
  type MissingRow,
  type ReconciliationAccount,
  ReconciliationBadge,
  type Transaction,
  useProject,
  useProjectMutations,
  useStatements,
  VerifyingBadge,
} from '@/modules/projects'
import { LazyPdfViewer } from '@/modules/projects/components/dialogs/LazyPdfViewer'
import { isReconciled, isSegmentHeldForTeam } from '@/modules/projects/status'
import { statementPageUrl } from '@/modules/projects/utils'
import { executeGet } from '@/services/lukita'
import { useDialogActions, useDialogData, useDialogIsOpen } from '@/stores'
import { cycleWorklist } from '../../reconciliation/cycleRows'
import {
  type AccountIdentity,
  accountKey,
  anchorEditBody,
  compareByPrintedOrder,
  groupRowsByAccount,
  periodLabel,
  rowsForAccount,
  suggestedCycleLast4,
} from '../../reconciliation/helpers'
import type { PdfViewerHandle } from '../PdfViewer'
import { AccountLedgerSection } from './AccountLedgerSection'
import { AccountSection } from './AccountSection'
import { CorrectionPanel } from './CorrectionPanel'
import { CycleSummarySection } from './CycleSummarySection'

const dialogId = 'reconciliation-workspace'

interface WorkspaceTarget {
  statementId: number
  segmentId: number
}

export const ReconciliationWorkspace = () => {
  const open = useDialogIsOpen(dialogId)
  const target = useDialogData() as WorkspaceTarget | undefined
  const { openDialog, closeDialog } = useDialogActions()
  const project = useProject()
  const { toast } = useToast()
  const isEntitled = useReconciliationEntitled()
  const {
    removeTransactions,
    restoreTransactions,
    createTransaction,
    updateTransaction,
    adjustReconciliationAnchor,
    setSegmentReconciled,
    checkInstruction,
    reExtractSegment,
    insertRows,
    undoInstruction,
  } = useProjectMutations()

  const statements = useStatements()

  // Every cycle on the reconciliation page, in the order it lists them, so previous / next walks
  // past the end of a statement into the next one.
  const worklist = cycleWorklist(statements)
  const position = worklist.findIndex(
    (cycle) => cycle.segmentId === target?.segmentId,
  )
  const goToCycle = (index: number) => {
    const cycle = worklist[index]
    if (cycle) openDialog(dialogId, cycle)
  }

  // Read the cycle live from the store so the gap and verdict refresh as the websocket pushes
  // lukita's recompute.
  const statement = statements.find((item) => item.id === target?.statementId)
  const segment = statement?.segments?.find(
    (item) => item.id === target?.segmentId,
  )
  const [rows, setRows] = useState<Transaction[]>([])
  // Which cycle the loaded rows belong to. Rows are the only thing the plain ledger has to group by,
  // so until they land it must not decide the cycle is empty, and it must not list the last cycle's.
  const [loadedSegmentId, setLoadedSegmentId] = useState<number | null>(null)
  const pdfRef = useRef<PdfViewerHandle>(null)

  // Move the PDF to a row's source page, or the cycle's first page when the row has none, so the
  // user always lands near the right spot.
  // A row on a page moved in from another upload opens that upload, since this pane shows only one.
  const jumpToPage = (page: number | null, fileUrl: string) => {
    if (page && fileUrl && fileUrl !== statement?.fileUrl) {
      window.open(statementPageUrl(fileUrl, page), '_blank', 'noopener')
      return
    }
    const destination = page ?? segment?.startPage ?? null
    if (destination != null) pdfRef.current?.goToPage(destination)
  }

  // Load the cycle's full row set (it carries the removed rows the grid hides). Re-fetch whenever the
  // cycle is re-broadcast (the `segment` reference changes on any UPDATE_STATEMENT) so the rows stay
  // in sync with lukita's recompute. The cleanup flag drops any out-of-order response. Rows are put
  // in printed order once here, so both the account and the summary sections list them that way.
  useEffect(() => {
    if (!open || !project?.slug || !target?.segmentId) return
    let active = true
    executeGet(
      `/projects/${project.slug}/statements/segments/${target.segmentId}/transactions`,
    )
      .then((response) => response.json())
      .then((data) => {
        if (active) {
          const loaded = camelcaseKeys(data) as Transaction[]
          setRows(loaded.sort(compareByPrintedOrder))
          setLoadedSegmentId(target.segmentId)
        }
      })
      .catch((error) => {
        // Mark the cycle settled even on failure, or the ledger sits on "Loading…" for good with
        // only a toast, which the user may already have dismissed, to say why.
        if (active) {
          setRows([])
          setLoadedSegmentId(target.segmentId)
        }
        toast({
          title: 'Could not load the cycle',
          description: (error as Error).message,
          theme: 'destructive',
        })
      })
    return () => {
      active = false
    }
  }, [open, project?.slug, target?.segmentId, segment, toast])

  const accounts = segment?.reconciliationDetail?.accounts ?? []

  if (!statement || !segment) return null

  // A cycle the engine balanced on its own has nothing to accept by hand, so the toggle only shows
  // when the cycle still needs a verdict or a manual one is already in place and can be reverted.
  const balanced = isReconciled(segment) && !segment.manuallyReconciled

  // Without the reconciliation feature there are no per-account balances to file rows under, so the
  // rows carry the grouping themselves.
  const rowsLoaded = loadedSegmentId === segment.id
  const rowGroups = !isEntitled && rowsLoaded ? groupRowsByAccount(rows) : []
  const showCycleSummary = isEntitled
    ? accounts.length === 0
    : rowsLoaded && rowGroups.length === 0

  const fail = (title: string, error: unknown) =>
    toast({
      title,
      description: (error as Error).message,
      theme: 'destructive',
    })

  const handleRemove = (row: Transaction) => {
    setRows((current) =>
      current.map((item) =>
        item.id === row.id
          ? { ...item, discardedAt: new Date().toISOString() }
          : item,
      ),
    )
    removeTransactions([row]).catch((error) => {
      setRows((current) =>
        current.map((item) =>
          item.id === row.id ? { ...item, discardedAt: null } : item,
        ),
      )
      fail('Could not remove the row', error)
    })
  }

  const handleRestore = (row: Transaction) => {
    setRows((current) =>
      current.map((item) =>
        item.id === row.id ? { ...item, discardedAt: null } : item,
      ),
    )
    restoreTransactions([row]).catch((error) => {
      setRows((current) =>
        current.map((item) =>
          item.id === row.id
            ? { ...item, discardedAt: new Date().toISOString() }
            : item,
        ),
      )
      fail('Could not restore the row', error)
    })
  }

  const handleEditAmount = (row: Transaction, amount: number) => {
    setRows((current) =>
      current.map((item) => (item.id === row.id ? { ...item, amount } : item)),
    )
    updateTransaction(row.id, { amount }).catch((error) => {
      setRows((current) =>
        current.map((item) =>
          item.id === row.id ? { ...item, amount: row.amount } : item,
        ),
      )
      fail('Could not update the amount', error)
    })
  }

  const handleAdd = (
    account: AccountIdentity,
    input: { date: string; description: string; amount: number },
  ) => {
    createTransaction({
      statement_id: statement.id,
      statement_segment_id: segment.id,
      account_number: account.last4,
      ...(account.shareNumber ? { share_number: account.shareNumber } : {}),
      ...input,
    })
      .then((created) => setRows((current) => [...current, created]))
      .catch((error) => fail('Could not add the row', error))
  }

  const handleEditAnchor = (
    account: ReconciliationAccount,
    side: 'begin' | 'end',
    next: number,
  ) => {
    adjustReconciliationAnchor(
      segment.id,
      anchorEditBody(account, side, next),
    ).catch((error) => fail('Could not update the balance', error))
  }

  // Mark the cycle reconciled by hand (or revert) for a statement the engine can't balance, like a
  // Cash App pass-through that prints no real balance so its rows never sum to the printed $0.
  const handleToggleReconciled = () => {
    const next = !segment.manuallyReconciled
    setSegmentReconciled(segment.id, next).catch((error) =>
      fail(
        next ? 'Could not mark the cycle reconciled' : 'Could not revert',
        error,
      ),
    )
  }

  const readOnlyTitle = !isEntitled || balanced

  const handleCheckInstruction = (instruction: string) =>
    checkInstruction(segment.id, instruction).catch((error) => {
      fail('Could not check the fix', error)
      return null
    })

  const handleReExtract = (instruction: string, scope: 'cycle' | 'family') =>
    reExtractSegment(segment.id, instruction, scope)
      .then(() => true)
      .catch((error) => {
        fail('Could not start the fix', error)
        return false
      })

  const handleInsertRows = (instruction: string, rows: MissingRow[]) =>
    insertRows(segment.id, instruction, rows)
      .then(() => true)
      .catch((error) => {
        fail('Could not add the missing lines', error)
        return false
      })

  const handleUndoInstruction = () =>
    undoInstruction(segment.id)
      .then(() => true)
      .catch((error) => {
        fail('Could not undo the fix', error)
        return false
      })

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => !next && closeDialog()}
    >
      <DialogPrimitive.Portal>
        <DialogOverlay className="bg-black/40" />
        <DialogPrimitive.Content className="fixed inset-0 z-50 flex flex-col bg-background">
          <div className="flex items-start justify-between gap-3 border-b p-4">
            <div className="min-w-0 space-y-1.5">
              <DialogTitle className="truncate text-base font-semibold text-foreground">
                {readOnlyTitle ? 'View cycle' : 'Review cycle'} —{' '}
                {statement.fileName}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {readOnlyTitle
                  ? "Check this billing cycle's transactions against the statement"
                  : 'Fix this billing cycle so its balance reconciles'}
              </DialogDescription>
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                {/* The range controls sit at the start of this row, not with the actions on the
                    right, so they hold one spot whether or not the accept button is showing. */}
                {position >= 0 && worklist.length > 1 && (
                  <div className="flex items-center gap-1">
                    <Button
                      theme="outline"
                      size="sm"
                      isIcon
                      className="cursor-pointer"
                      disabled={position === 0}
                      onClick={() => goToCycle(position - 1)}
                    >
                      <ChevronLeftIcon className="size-4" />
                      <span className="sr-only">Previous range</span>
                    </Button>
                    <span className="w-[4.5rem] text-center text-xs tabular-nums">
                      {position + 1} of {worklist.length}
                    </span>
                    <Button
                      theme="outline"
                      size="sm"
                      isIcon
                      className="cursor-pointer"
                      disabled={position === worklist.length - 1}
                      onClick={() => goToCycle(position + 1)}
                    >
                      <ChevronRightIcon className="size-4" />
                      <span className="sr-only">Next range</span>
                    </Button>
                  </div>
                )}
                <span>{periodLabel(segment)}</span>
                {isSegmentHeldForTeam(segment) ? (
                  <VerifyingBadge />
                ) : (
                  isEntitled && (
                    <ReconciliationBadge
                      status={segment.reconciliationStatus}
                    />
                  )
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* The pane on the left shows only this cycle's pages; this opens the whole file. */}
              {statement.fileUrl && (
                <Button
                  theme="outline"
                  size="sm"
                  className="cursor-pointer gap-1.5"
                  asChild
                >
                  <a
                    href={statementPageUrl(
                      statement.fileUrl,
                      segment.startPage,
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open the full PDF in a new tab"
                  >
                    <ExternalLinkIcon className="size-3.5" />
                    Open PDF
                  </a>
                </Button>
              )}
              {isEntitled && !balanced && (
                <Button
                  theme={segment.manuallyReconciled ? 'ghost' : 'outline'}
                  size="sm"
                  className="cursor-pointer gap-1.5"
                  onClick={handleToggleReconciled}
                >
                  {segment.manuallyReconciled ? (
                    <>
                      <RotateCcwIcon className="size-3.5" />
                      Revert to engine verdict
                    </>
                  ) : (
                    <>
                      <CircleCheckIcon className="size-3.5" />
                      Mark cycle as reconciled
                    </>
                  )}
                </Button>
              )}
              <DialogPrimitive.Close asChild>
                <Button
                  theme="ghost"
                  size="sm"
                  isIcon
                  className="cursor-pointer"
                >
                  <XIcon className="size-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </DialogPrimitive.Close>
            </div>
          </div>

          <div className="flex min-h-0 flex-1">
            {/* Left pane: only the cycle's pages of the source document, so a cycle inside a huge
                PDF stays short. A row's date click jumps to that row's page. */}
            <div className="w-1/2 border-r">
              {statement.fileUrl ? (
                <Suspense
                  fallback={
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                      Loading the document…
                    </div>
                  }
                >
                  <LazyPdfViewer
                    ref={pdfRef}
                    url={statement.fileUrl}
                    initialPage={segment.startPage}
                    pageRange={
                      segment.startPage != null && segment.endPage != null
                        ? { start: segment.startPage, end: segment.endPage }
                        : null
                    }
                  />
                </Suspense>
              ) : (
                <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
                  No source document is attached to this statement.
                </div>
              )}
            </div>

            {/* Right pane: the editable rows for the cycle, plus its printed balances when the
                account is entitled to have them read. */}
            <div className="w-1/2 space-y-3 overflow-auto p-4 scrollbar-thin scrollbar-thumb-neutral-300">
              {isEntitled && (
                <CorrectionPanel
                  key={segment.id}
                  segment={segment}
                  rowCount={
                    rowsLoaded
                      ? rows.filter((row) => !row.discardedAt).length
                      : null
                  }
                  onCheck={handleCheckInstruction}
                  onReExtract={handleReExtract}
                  onInsertRows={handleInsertRows}
                  onUndo={handleUndoInstruction}
                />
              )}

              {isEntitled && accounts.length > 0 && (
                <>
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <PencilIcon className="size-3 shrink-0" />
                    Click any balance or amount to edit it.
                  </p>
                  {accounts.map((account) => (
                    <AccountSection
                      key={accountKey(account)}
                      account={account}
                      // Until this cycle's rows land, `rows` still holds the last cycle's, and they
                      // match on last4 alone, so an account both cycles share would list them.
                      rows={rowsLoaded ? rowsForAccount(rows, account) : []}
                      onEditAmount={handleEditAmount}
                      onRemove={handleRemove}
                      onRestore={handleRestore}
                      onAdd={(input) => handleAdd(account, input)}
                      onEditAnchor={(side, next) =>
                        handleEditAnchor(account, side, next)
                      }
                      onJump={jumpToPage}
                    />
                  ))}
                </>
              )}

              {!isEntitled && !rowsLoaded && (
                <p className="py-6 text-center text-xs text-muted-foreground">
                  Loading this cycle's transactions…
                </p>
              )}

              {rowGroups.map((group) => (
                <AccountLedgerSection
                  key={group.key}
                  last4={group.last4}
                  shareNumber={group.shareNumber}
                  rows={group.rows}
                  onEditAmount={handleEditAmount}
                  onRemove={handleRemove}
                  onRestore={handleRestore}
                  onAdd={(input) =>
                    handleAdd(
                      { last4: group.last4, shareNumber: group.shareNumber },
                      input,
                    )
                  }
                  onJump={jumpToPage}
                />
              ))}

              {showCycleSummary && (
                <CycleSummarySection
                  segment={segment}
                  rows={rows}
                  suggestedLast4={suggestedCycleLast4(statement, segment)}
                  showBalances={isEntitled}
                  onEditAmount={handleEditAmount}
                  onRemove={handleRemove}
                  onRestore={handleRestore}
                  onAdd={(last4, input) => handleAdd({ last4 }, input)}
                  onJump={jumpToPage}
                />
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

ReconciliationWorkspace.id = dialogId
